import { rectsOverlap, type Rect2D, type Vec2 } from "./math2d.js";

/** What a body is for, which decides how it is integrated and resolved. */
export type BodyType =
  /** Never moves and is never pushed. Walls, floors, the world. */
  | "static"
  /** Moved by code, pushes dynamics, is not pushed back. Lifts, doors. */
  | "kinematic"
  /** Moved by the simulation and pushed out of overlaps. */
  | "dynamic";

export interface Body2D {
  /** Whatever the game wants to get back from a hit. */
  owner?: unknown;
  type: BodyType;
  /** Center in world space. */
  x: number;
  y: number;
  width: number;
  height: number;
  velocityX: number;
  velocityY: number;
  /** Multiplies the world gravity for this body. 0 for a floating one. */
  gravityScale?: number;
  /** Bounce, 0..1. */
  restitution?: number;
  /** Ground friction applied per second while touching something below. */
  friction?: number;
  /** Overlaps are reported but never resolved. A trigger volume. */
  sensor?: boolean;
  /** Bitmask: two bodies interact when each is in the other's mask. */
  layer?: number;
  mask?: number;
  /** Set by the solver each step. */
  touchingGround?: boolean;
}

/** One overlap the solver found. */
export interface Contact2D {
  a: Body2D;
  b: Body2D;
  /** Unit vector pushing `a` out of `b`. */
  normalX: number;
  normalY: number;
  /** How deep the overlap was, along the normal. */
  depth: number;
}

export interface World2DOptions {
  gravityX?: number;
  gravityY?: number;
  /**
   * Broadphase cell size. Best at roughly the size of a typical body: much
   * smaller and one body spans many cells, much larger and every body lands
   * in the same one and the broadphase stops narrowing anything.
   */
  cellSize?: number;
  /** Speed below which a body is snapped to rest, to stop endless jitter. */
  sleepSpeed?: number;
}

const DEFAULT_CELL = 64;
const DEFAULT_SLEEP = 0.5;

export const boundsOf = (body: Body2D): Rect2D => ({
  x: body.x - body.width / 2,
  y: body.y - body.height / 2,
  width: body.width,
  height: body.height,
});

/** True when two bodies' layers and masks let them see each other. */
export function layersInteract(a: Body2D, b: Body2D): boolean {
  const aLayer = a.layer ?? 1;
  const bLayer = b.layer ?? 1;
  const aMask = a.mask ?? 0xffffffff;
  const bMask = b.mask ?? 0xffffffff;
  return (aMask & bLayer) !== 0 && (bMask & aLayer) !== 0;
}

/**
 * An axis-aligned 2D physics world.
 *
 * AABB-only, deliberately. Rotated shapes need SAT, a contact manifold, and an
 * impulse solver, and the moment a game has those it wants joints and stacking
 * and it should be using a real physics library. What the overwhelming
 * majority of 2D games actually need is boxes that do not overlap, and that is
 * what this does — well, and predictably.
 *
 * Bodies are separated along the axis of **least** penetration, which is what
 * makes walking over a row of tiles work: a body a pixel into the floor is
 * pushed up, not sideways into the next tile, so it does not catch on seams.
 *
 * Movement is resolved one axis at a time, x then y. Doing both at once from a
 * single overlap makes a body sliding along a wall stick at every seam, and it
 * is the single most common bug in a hand-rolled tile collider.
 */
export class World2D {
  gravityX: number;
  gravityY: number;
  cellSize: number;
  sleepSpeed: number;

  #bodies: Body2D[] = [];
  /** Broadphase buckets, rebuilt each step. */
  #grid = new Map<number, Body2D[]>();
  #contacts: Contact2D[] = [];

  constructor(options: World2DOptions = {}) {
    this.gravityX = options.gravityX ?? 0;
    this.gravityY = options.gravityY ?? 900;
    this.cellSize = Math.max(1, options.cellSize ?? DEFAULT_CELL);
    this.sleepSpeed = options.sleepSpeed ?? DEFAULT_SLEEP;
  }

  get bodies(): readonly Body2D[] {
    return this.#bodies;
  }

  /** The overlaps found on the last step, sensors included. */
  get contacts(): readonly Contact2D[] {
    return this.#contacts;
  }

  add(body: Body2D): Body2D {
    this.#bodies.push(body);
    return body;
  }

  remove(body: Body2D): boolean {
    const i = this.#bodies.indexOf(body);
    if (i < 0) return false;
    this.#bodies.splice(i, 1);
    return true;
  }

  clear(): void {
    this.#bodies.length = 0;
    this.#contacts.length = 0;
  }

  /**
   * Advance the world by `dt` seconds.
   *
   * Meant to be called from a fixed-timestep loop. Integrating this against a
   * variable delta makes the simulation depend on the display's refresh rate,
   * which is exactly what `Loop` exists to prevent.
   */
  step(dt: number): void {
    this.#contacts.length = 0;

    for (const body of this.#bodies) {
      if (body.type !== "dynamic") continue;
      const scale = body.gravityScale ?? 1;
      body.velocityX += this.gravityX * scale * dt;
      body.velocityY += this.gravityY * scale * dt;
      body.touchingGround = false;
    }

    // One axis at a time. Moving in both and then resolving from a single
    // overlap is what makes a body catch on the seam between two floor tiles.
    this.#moveAxis(dt, "x");
    this.#moveAxis(dt, "y");

    for (const body of this.#bodies) {
      if (body.type !== "dynamic") continue;
      if (body.touchingGround && body.friction) {
        const keep = Math.max(0, 1 - body.friction * dt);
        body.velocityX *= keep;
      }
      if (Math.abs(body.velocityX) < this.sleepSpeed) body.velocityX = 0;
      if (Math.abs(body.velocityY) < this.sleepSpeed) body.velocityY = 0;
    }
  }

  #moveAxis(dt: number, axis: "x" | "y"): void {
    for (const body of this.#bodies) {
      if (body.type === "static") continue;
      if (axis === "x") body.x += body.velocityX * dt;
      else body.y += body.velocityY * dt;
    }

    this.#rebuildGrid();

    for (const body of this.#bodies) {
      if (body.type !== "dynamic") continue;
      for (const other of this.#near(body)) {
        if (other === body) continue;
        if (!layersInteract(body, other)) continue;
        if (!rectsOverlap(boundsOf(body), boundsOf(other))) continue;

        const contact = contactBetween(body, other);
        if (!contact) continue;
        this.#contacts.push(contact);

        // A sensor reports and does nothing. That is the whole difference
        // between a trigger volume and a wall.
        if (body.sensor || other.sensor) continue;
        this.#separate(body, other, contact, axis);
      }
    }
  }

  /** Push `body` out of `other` and kill the velocity that drove them together. */
  #separate(body: Body2D, other: Body2D, contact: Contact2D, axis: "x" | "y"): void {
    if (axis === "x") {
      if (contact.normalX === 0) return;
      body.x += contact.normalX * contact.depth;
      const bounce = body.restitution ?? 0;
      if (Math.sign(body.velocityX) === -Math.sign(contact.normalX)) {
        body.velocityX = bounce > 0 ? -body.velocityX * bounce : 0;
      }
      return;
    }
    if (contact.normalY === 0) return;
    body.y += contact.normalY * contact.depth;
    const bounce = body.restitution ?? 0;
    if (Math.sign(body.velocityY) === -Math.sign(contact.normalY)) {
      body.velocityY = bounce > 0 ? -body.velocityY * bounce : 0;
    }
    // Pushed upward means something solid is underneath.
    if (contact.normalY < 0) body.touchingGround = true;
    void other;
  }

  // --- broadphase ----------------------------------------------------------

  #key(column: number, row: number): number {
    // Two 16-bit halves in one number: a Map of numbers is markedly faster to
    // hash than a Map of "3,7" strings, and this is the innermost loop.
    return ((column & 0xffff) << 16) | (row & 0xffff);
  }

  #rebuildGrid(): void {
    this.#grid.clear();
    for (const body of this.#bodies) {
      const b = boundsOf(body);
      const c0 = Math.floor(b.x / this.cellSize);
      const r0 = Math.floor(b.y / this.cellSize);
      const c1 = Math.floor((b.x + b.width) / this.cellSize);
      const r1 = Math.floor((b.y + b.height) / this.cellSize);
      for (let r = r0; r <= r1; r++) {
        for (let c = c0; c <= c1; c++) {
          const key = this.#key(c, r);
          let bucket = this.#grid.get(key);
          if (!bucket) this.#grid.set(key, (bucket = []));
          bucket.push(body);
        }
      }
    }
  }

  /** Bodies sharing a cell with this one, each at most once. */
  #near(body: Body2D): Body2D[] {
    const b = boundsOf(body);
    const c0 = Math.floor(b.x / this.cellSize);
    const r0 = Math.floor(b.y / this.cellSize);
    const c1 = Math.floor((b.x + b.width) / this.cellSize);
    const r1 = Math.floor((b.y + b.height) / this.cellSize);
    const seen = new Set<Body2D>();
    const out: Body2D[] = [];
    for (let r = r0; r <= r1; r++) {
      for (let c = c0; c <= c1; c++) {
        for (const other of this.#grid.get(this.#key(c, r)) ?? []) {
          if (seen.has(other)) continue;
          seen.add(other);
          out.push(other);
        }
      }
    }
    return out;
  }

  // --- queries -------------------------------------------------------------

  /** Every body containing a world point. */
  pointCast(point: Vec2): Body2D[] {
    return this.#bodies.filter((body) => {
      const b = boundsOf(body);
      return (
        point.x >= b.x && point.x < b.x + b.width && point.y >= b.y && point.y < b.y + b.height
      );
    });
  }

  /** Every body overlapping a world rectangle. */
  areaCast(area: Rect2D): Body2D[] {
    return this.#bodies.filter((body) => rectsOverlap(boundsOf(body), area));
  }

  /**
   * The first body a ray hits, by the slab method.
   *
   * Returns where it hit and how far along, so a caller can place an impact
   * effect without repeating the maths.
   */
  rayCast(
    origin: Vec2,
    direction: Vec2,
    maxDistance = Infinity,
    filter?: (body: Body2D) => boolean,
  ): { body: Body2D; point: Vec2; distance: number } | null {
    let best: { body: Body2D; point: Vec2; distance: number } | null = null;

    for (const body of this.#bodies) {
      if (filter && !filter(body)) continue;
      const b = boundsOf(body);
      const hit = raySlab(origin, direction, b);
      if (hit === null || hit > maxDistance) continue;
      if (best && hit >= best.distance) continue;
      best = {
        body,
        distance: hit,
        point: { x: origin.x + direction.x * hit, y: origin.y + direction.y * hit },
      };
    }
    return best;
  }
}

/**
 * The overlap between two boxes, resolved along the axis of least penetration.
 *
 * Least, not first: a body one pixel into the floor overlaps deeply in x and
 * barely in y, and pushing it along x would fling it sideways. The shallow
 * axis is always the one it came in through.
 */
export function contactBetween(a: Body2D, b: Body2D): Contact2D | null {
  const ab = boundsOf(a);
  const bb = boundsOf(b);
  const overlapX = Math.min(ab.x + ab.width, bb.x + bb.width) - Math.max(ab.x, bb.x);
  const overlapY = Math.min(ab.y + ab.height, bb.y + bb.height) - Math.max(ab.y, bb.y);
  if (overlapX <= 0 || overlapY <= 0) return null;

  if (overlapX < overlapY) {
    const sign = a.x < b.x ? -1 : 1;
    return { a, b, normalX: sign, normalY: 0, depth: overlapX };
  }
  const sign = a.y < b.y ? -1 : 1;
  return { a, b, normalX: 0, normalY: sign, depth: overlapY };
}

/** Distance along a ray to a box, or null when it misses. */
export function raySlab(origin: Vec2, direction: Vec2, box: Rect2D): number | null {
  let near = -Infinity;
  let far = Infinity;

  for (const axis of ["x", "y"] as const) {
    const o = axis === "x" ? origin.x : origin.y;
    const d = axis === "x" ? direction.x : direction.y;
    const lo = axis === "x" ? box.x : box.y;
    const hi = lo + (axis === "x" ? box.width : box.height);

    if (Math.abs(d) < 1e-8) {
      // Parallel to this pair of slabs: a miss unless it starts between them.
      if (o < lo || o > hi) return null;
      continue;
    }
    let t0 = (lo - o) / d;
    let t1 = (hi - o) / d;
    if (t0 > t1) [t0, t1] = [t1, t0];
    near = Math.max(near, t0);
    far = Math.min(far, t1);
    if (near > far) return null;
  }
  if (far < 0) return null;
  return near >= 0 ? near : 0;
}
